<template>
    <div class="safe-froala-wrapper">
        <froala
            v-if="shouldRender"
            :value="computedFroalaValue"
            :tag="tag"
            :config="mergedConfig"
            @update:value="handleUpdate"
            @initialized="handleInitialized"
            @error="handleError"
        />
    </div>
</template>

<script>
export default {
    name: 'SafeFroalaWrapper',

    props: {
        value: {
            type: String,
            default: '',
        },

        tag: {
            type: String,
            default: 'textarea',
        },

        config: {
            type: Object,
            default: () => ({}),
        },
    },

    emits: ['update:value', 'initialized', 'error'],

    data() {
        return {
            shouldRender: false,
            internalValue: '',
            isInitialized: false,
            pendingValue: null,
            userIsTyping: false,
            typingTimeout: null,
            froalaValue: '', // Separate value for Froala to prevent unwanted updates
        };
    },

    computed: {
        // Computed property to control what value Froala receives
        computedFroalaValue() {
            // If user is typing, don't change the Froala value
            if (this.userIsTyping) {
                return this.froalaValue;
            }
            // Otherwise, use the internal value
            return this.internalValue;
        },

        mergedConfig() {
            const vm = this;
            const blurHandler = this.config?.events?.blur;

            return {
                ...this.config,
                events: {
                    ...this.config.events,
                    initialized: () => {
                        this.isInitialized = true;

                        // Set any pending value
                        if (this.pendingValue !== null) {
                            this.internalValue = this.pendingValue;
                            this.froalaValue = this.pendingValue;
                            this.pendingValue = null;
                        }

                        this.$emit('initialized');
                    },
                    contentChanged: () => {
                        this.markUserTyping();
                    },
                    keydown: () => {
                        this.markUserTyping();
                    },
                    input: () => {
                        this.markUserTyping();
                    },
                    'paste.before': () => {
                        this.markUserTyping();
                    },
                    blur(...args) {
                        blurHandler?.apply(this, args);

                        if (this?.codeView?.isActive?.()) {
                            setTimeout(() => {
                                vm.handleCodeViewBlur(this);
                            }, 0);
                        }
                    },
                },
            };
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                // Only update if the new value is actually different from the current internal value
                // This prevents the editor from being cleared when the same value is set during autosave
                if (newValue !== this.internalValue) {
                    if (this.isInitialized) {
                        this.internalValue = newValue || '';
                        // Only update froalaValue if user is not typing
                        if (!this.userIsTyping) {
                            this.froalaValue = newValue || '';
                        }
                    } else {
                        this.pendingValue = newValue || '';
                    }
                }
            },
        },
    },

    mounted() {
        // Initialize internal value immediately to prevent race conditions
        this.internalValue = this.value || '';
        this.froalaValue = this.value || '';

        // Delay rendering to avoid race conditions
        this.$nextTick(() => {
            setTimeout(() => {
                this.shouldRender = true;
            }, 50);
        });
    },

    beforeUnmount() {
        // Clean up timeout
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
    },

    methods: {
        markUserTyping() {
            // User is actively editing
            this.userIsTyping = true;

            // Clear any existing timeout
            if (this.typingTimeout) {
                clearTimeout(this.typingTimeout);
            }

            // Set a timeout to mark user as no longer typing
            this.typingTimeout = setTimeout(() => {
                this.userIsTyping = false;
            }, 2000); // 2 seconds after last change
        },

        handleUpdate(value) {
            // Only update and emit if the value has actually changed
            if (this.internalValue !== value) {
                this.internalValue = value;
                this.froalaValue = value; // Keep froalaValue in sync when user types
                this.$emit('update:value', value);
            }
        },

        handleCodeViewBlur(editor) {
            const value = editor?.codeView?.get?.();

            if (typeof value !== 'string') {
                return;
            }

            this.markUserTyping();
            this.handleUpdate(value);
        },

        handleInitialized() {
            this.isInitialized = true;
            this.$emit('initialized');
        },

        handleError(error) {
            this.$emit('error', error);
        },
    },
};
</script>

<style scoped>
.safe-froala-wrapper {
    min-height: 60px;
}
:deep(.fr-box) {
    color: black
}
</style>
